import React, { useMemo, useState } from 'react';
import './JsonUrlViewer.css';

function isPrimitive(value) {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value);
}

function formatCell(value) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch (e) {
    return '[unserializable]';
  }
}

function collectHeaders(items) {
  const set = new Set();
  items.forEach((item) => {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      Object.keys(item).forEach((k) => set.add(k));
    }
  });
  return Array.from(set);
}

function normalizeFetchUrl(rawUrl) {
  const trimmed = (rawUrl || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('/tmp/') || trimmed.startsWith('tmp/') || trimmed.startsWith('/data/') || trimmed.startsWith('data/')) {
    const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `/api/v1/document-intake/dev/json-file?path=${encodeURIComponent(normalizedPath)}`;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  if (trimmed.startsWith('/')) return trimmed;
  return `/${trimmed}`;
}

function renderMappedParagraphText(value) {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

function MappingValidationTable({ rows }) {
  if (!rows || rows.length === 0) return null;

  return (
    <div className="json-card json-priority-card">
      <h3>Control-to-Description Validation View <span className="json-count">({rows.length})</span></h3>
      <div className="json-table-wrap">
        <table className="json-table">
          <thead>
            <tr>
              <th>internal_control_id</th>
              <th>internal_control_statement</th>
              <th className="json-col-mapped-text">mapped_paragraph_text_ai</th>
              <th>page_number_ai</th>
              <th>paragraph_id_ai</th>
              <th>selected_paragraph_ids_ai</th>
              <th>paragraph_tags_app</th>
              <th>pre_ai_confidence_app</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={`map-row-${idx}`}>
                <td>{formatCell(row.internal_control_id)}</td>
                <td>{formatCell(row.internal_control_statement)}</td>
                <td className="json-cell-mapped-text">
                  <pre className="json-mapped-text">{renderMappedParagraphText(row.mapped_paragraph_text_ai)}</pre>
                </td>
                <td>{formatCell(row.page_number_ai)}</td>
                <td>{formatCell(row.paragraph_id_ai)}</td>
                <td>{formatCell(row.selected_paragraph_ids_ai)}</td>
                <td>{formatCell(row.paragraph_tags_app)}</td>
                <td>{formatCell(row.pre_ai_confidence_app)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JsonArrayTable({ title, items }) {
  const headers = useMemo(() => collectHeaders(items), [items]);

  if (items.length === 0) {
    return (
      <div className="json-card">
        <h3>{title}</h3>
        <div className="json-empty">No rows</div>
      </div>
    );
  }

  const allPrimitive = items.every((it) => isPrimitive(it));
  if (allPrimitive) {
    return (
      <div className="json-card">
        <h3>{title}</h3>
        <table className="json-table">
          <thead>
            <tr>
              <th>#</th>
              <th>value</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr key={`${title}-${idx}`}>
                <td>{idx + 1}</td>
                <td>{formatCell(it)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (headers.length === 0) {
    return (
      <div className="json-card">
        <h3>{title}</h3>
        <div className="json-empty">Array contains nested values. See raw JSON below.</div>
      </div>
    );
  }

  return (
    <div className="json-card">
      <h3>{title} <span className="json-count">({items.length})</span></h3>
      <div className="json-table-wrap">
        <table className="json-table">
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={`${title}-h-${h}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={`${title}-r-${idx}`}>
                {headers.map((h) => (
                  <td key={`${title}-${idx}-${h}`}>{formatCell(item?.[h])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JsonObjectTable({ title, obj }) {
  const entries = Object.entries(obj || {});
  if (entries.length === 0) {
    return (
      <div className="json-card">
        <h3>{title}</h3>
        <div className="json-empty">No fields</div>
      </div>
    );
  }

  return (
    <div className="json-card">
      <h3>{title}</h3>
      <table className="json-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={`${title}-${k}`}>
              <td>{k}</td>
              <td>{formatCell(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function JsonUrlViewer({ initialUrl = '' }) {
  const [urlInput, setUrlInput] = useState(initialUrl);
  const [loadedUrl, setLoadedUrl] = useState(initialUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const fetchJson = async (rawUrl) => {
    const targetUrl = normalizeFetchUrl(rawUrl);
    if (!targetUrl) {
      setError('Provide a JSON URL/path.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(targetUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const parsed = await response.json();
      setData(parsed);
      setLoadedUrl(targetUrl);
      window.history.replaceState({}, '', `/json-view/${encodeURIComponent(targetUrl)}`);
    } catch (err) {
      setData(null);
      setError(`Failed to load JSON from "${targetUrl}": ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (initialUrl) {
      fetchJson(initialUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topLevelCards = useMemo(() => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
    const cards = [];
    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        cards.push({ type: 'array', key, value });
      } else if (value && typeof value === 'object') {
        cards.push({ type: 'object', key, value });
      }
    });
    return cards;
  }, [data]);

  const mappingRows = useMemo(() => {
    const paragraphTagMap = new Map();
    const paragraphTextMap = new Map();
    const paragraphSource = Array.isArray(data?.paragraphs) ? data.paragraphs : [];
    paragraphSource.forEach((p) => {
      const id = p?.paragraph_id;
      if (!id) return;
      const tags = Array.isArray(p?.paragraph_tags_app)
        ? p.paragraph_tags_app.map((t) => String(t).trim()).filter(Boolean)
        : [];
      paragraphTagMap.set(id, tags);
      paragraphTextMap.set(id, typeof p?.text === 'string' ? p.text.trim() : '');
    });

    const normalizeIds = (value) => {
      if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
      if (typeof value === 'string') {
        return value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      }
      return [];
    };

    const source = Array.isArray(data) ? data : data?.mappings;
    if (!Array.isArray(source)) return [];
    return source.map((item) => {
      const selectedIds = normalizeIds(item?.selected_paragraph_ids_ai);
      const mappedParagraphText = (() => {
        if (selectedIds.length > 0) {
          const lines = selectedIds.map((id) => {
            const text = paragraphTextMap.get(id);
            return text ? `[${id}] ${text}` : `[${id}]`;
          });
          return lines.join('\n\n');
        }
        if (item?.paragraph_id_ai) {
          const fallbackText = paragraphTextMap.get(item.paragraph_id_ai);
          if (fallbackText) return `[${item.paragraph_id_ai}] ${fallbackText}`;
        }
        return item?.mapped_paragraph_text_ai;
      })();

      return {
        internal_control_id: item?.internal_control_id ?? item?.internal_control_id_ai,
        internal_control_statement:
          item?.internal_control_statement ?? item?.internal_control_statement_ai,
        mapped_paragraph_text_ai: mappedParagraphText,
        page_number_ai: item?.page_number_ai,
        paragraph_id_ai: item?.paragraph_id_ai,
        selected_paragraph_ids_ai: Array.isArray(item?.selected_paragraph_ids_ai)
          ? item.selected_paragraph_ids_ai.join(', ')
          : item?.selected_paragraph_ids_ai,
        paragraph_tags_app: (() => {
        const selectedTags = Array.from(
          new Set(
            selectedIds.flatMap((id) => paragraphTagMap.get(id) || [])
          )
        );
        if (selectedTags.length > 0) return selectedTags.join(', ');
        const fallbackTags = paragraphTagMap.get(item?.paragraph_id_ai) || [];
        return fallbackTags.length > 0 ? fallbackTags.join(', ') : null;
        })(),
        pre_ai_confidence_app: item?.pre_ai_confidence_app,
      };
    });
  }, [data]);

  return (
    <div className="json-viewer-page">
      <div className="json-toolbar">
        <h2>JSON Viewer</h2>
        <div className="json-input-row">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Enter URL or path, e.g. /tmp/.../file.json"
          />
          <button onClick={() => fetchJson(urlInput)} disabled={loading}>
            {loading ? 'Loading...' : 'Load JSON'}
          </button>
        </div>
        {loadedUrl ? <div className="json-loaded-url">Loaded: <code>{loadedUrl}</code></div> : null}
        {error ? <div className="json-error">{error}</div> : null}
      </div>

      <MappingValidationTable rows={mappingRows} />

      {Array.isArray(data) ? <JsonArrayTable title="Root Array" items={data} /> : null}

      {!Array.isArray(data) && data && typeof data === 'object' ? (
        <div className="json-grid">
          {topLevelCards.map((card) =>
            card.type === 'array' ? (
              <JsonArrayTable key={`card-${card.key}`} title={card.key} items={card.value} />
            ) : (
              <JsonObjectTable key={`card-${card.key}`} title={card.key} obj={card.value} />
            )
          )}
          <JsonObjectTable title="Top-level fields" obj={Object.fromEntries(Object.entries(data).filter(([, v]) => isPrimitive(v)))} />
        </div>
      ) : null}

      {data ? (
        <details className="json-raw">
          <summary>Raw JSON</summary>
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </details>
      ) : null}
    </div>
  );
}
